import axios from 'axios';
import config from '../config';
import type {
  IntegrationStatusSnapshot,
  StocknoxIntegrationSchedules,
} from '../types/integration';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export type CombinedIntegrationStatus = {
  mirakel: IntegrationStatusSnapshot;
  stocknoxReachable?: boolean;
  stocknoxError?: string | null;
  stocknox: {
    generatedAt?: string;
    stockPublish?: {
      pending?: number;
      deadLetters?: number;
      oldestPendingAt?: string | null;
    };
    delivery?: {
      pendingInboxCount?: number;
      failedInboxCount?: number;
      deadLetterCount?: number;
    };
    reconciliation?: {
      lastRunAt?: string | null;
      lastRunStatus?: string | null;
      openDifferenceCount?: number;
    };
    productSync?: {
      lastSuccessfulSyncAt?: string | null;
      pendingCount?: number;
      failedCount?: number;
    };
    schedules?: StocknoxIntegrationSchedules;
  } | null;
};

export async function fetchIntegrationStatus(): Promise<CombinedIntegrationStatus> {
  const response = await axios.get<CombinedIntegrationStatus>(
    `${config.API_URL}/integrations/status`,
    { headers: authHeaders() },
  );
  return response.data;
}

export async function runDeliveryPoller(): Promise<{
  checkpoint: string;
  forwarded: number;
  skipped?: boolean;
  skipReason?: string;
}> {
  const response = await axios.post<{
    checkpoint: string;
    forwarded: number;
    skipped?: boolean;
    skipReason?: string;
  }>(
    `${config.API_URL}/integrations/delivery-poller/run`,
    {},
    { headers: authHeaders() },
  );
  return response.data;
}

export async function armDeliveryPoller(startAt?: string): Promise<{
  checkpointValue: string;
  armedAt: string;
  message: string;
  pollerEnabled: boolean;
}> {
  const response = await axios.post(
    `${config.API_URL}/integrations/delivery-poller/arm`,
    startAt ? { startAt } : {},
    { headers: authHeaders() },
  );
  return response.data;
}

export async function runStockPublish(companyId?: number): Promise<unknown> {
  const response = await axios.post(
    `${config.API_URL}/integrations/stock-publish/run`,
    { companyId },
    { headers: authHeaders() },
  );
  return response.data;
}

export type ReconciliationDifferenceItem = {
  pluNo: string;
  fortnoxAvailable: number;
  viskanBalance: number;
  diff: number;
};

export type ReconciliationRunResult = {
  runId: number;
  differenceCount: number;
  enqueuedCount?: number;
  differences: ReconciliationDifferenceItem[];
  publishSummary?: unknown;
};

export async function runProductSync(companyId?: number): Promise<unknown> {
  const response = await axios.post(
    `${config.API_URL}/integrations/product-sync/run`,
    { companyId },
    { headers: authHeaders() },
  );
  return response.data;
}

export async function runReconciliation(
  companyId?: number,
  mode: 'read_only' | 'repair' = 'read_only',
): Promise<ReconciliationRunResult> {
  const response = await axios.post<ReconciliationRunResult>(
    `${config.API_URL}/integrations/reconciliation/run`,
    { companyId, mode },
    { headers: authHeaders() },
  );
  return response.data;
}
